/**
 * Lazy-loading Nym mixnet client for the Privacy Switch demo.
 *
 * The `@nymproject/mix-fetch-full-fat` package requires cross-origin isolation
 * (SharedArrayBuffer). If the page isn't cross-origin isolated, the module
 * provides a clear error message instead of silently hanging.
 */

type MixFetchFn = (url: string, args?: RequestInit) => Promise<Response>;

// Singleton state
let nymMixFetch: MixFetchFn | null = null;
let nymStatus: "idle" | "loading" | "ready" | "error" | "no_coi" = "idle";
let nymError: string | null = null;
let bootstrapAbort: AbortController | null = null;

export function getNymState(): { status: string; error: string | null } {
  return { status: nymStatus, error: nymError };
}

export function isNymReady(): boolean {
  return nymStatus === "ready" && nymMixFetch !== null;
}

export function getNymMixFetch(): MixFetchFn | null {
  return nymMixFetch;
}

/**
 * Bootstrap the Nym mixFetch client.
 * Called lazily on first switch to Nym mode.
 */
export async function bootstrapNym(
  onProgress?: (msg: string) => void,
): Promise<MixFetchFn> {
  if (nymMixFetch) {
    return nymMixFetch;
  }

  if (nymStatus === "loading") {
    throw new Error("Mixnet bootstrap already in progress");
  }

  // Check for cross-origin isolation *before* doing the ~25MB import
  if (typeof crossOriginIsolated !== "undefined" && !crossOriginIsolated) {
    nymStatus = "no_coi";
    nymError = "Cross-origin isolation is required for the Nym mixnet client. " +
      "The hosting server must send Cross-Origin-Opener-Policy: same-origin " +
      "and Cross-Origin-Embedder-Policy: require-corp headers.";
    throw new Error(
      "This page is not cross-origin isolated. The Nym WASM client requires " +
      "SharedArrayBuffer which is only available with COOP/COEP headers. " +
      "Deploy to a host that sends these headers (see PRD section 8, C-2).",
    );
  }

  nymStatus = "loading";
  nymError = null;
  bootstrapAbort = new AbortController();
  const signal = bootstrapAbort.signal;

  try {
    onProgress?.("Initializing mixnet client…");

    if (signal.aborted) throw new DOMException("Aborted", "AbortError");

    const mod = await import(
      "@nymproject/mix-fetch-full-fat"
    ) as unknown as {
      createMixFetch?: (
        opts?: Record<string, unknown>,
      ) => Promise<{ mixFetch: MixFetchFn }>;
      mixFetch?: MixFetchFn;
    };

    if (signal.aborted) throw new DOMException("Aborted", "AbortError");

    if (typeof mod.createMixFetch === "function") {
      onProgress?.("Connecting to mixnet gateway (may take 10–30s)…");
      try {
        const client = await mod.createMixFetch({
          mixFetchOverride: { requestTimeoutMs: 60000 },
        });
        nymMixFetch = client.mixFetch;
      } catch (createErr) {
        throw new Error(
          `Failed to initialise mixnet: ${(createErr as Error).message}`,
        );
      }
    } else if (typeof mod.mixFetch === "function") {
      const raw = mod.mixFetch;
      nymMixFetch = async (url, args) => raw(url, args);
    } else {
      const exports = Object.keys(mod as Record<string, unknown>).join(", ");
      throw new Error(
        `Neither createMixFetch nor mixFetch found. Available: ${exports}`,
      );
    }

    if (signal.aborted) throw new DOMException("Aborted", "AbortError");

    nymStatus = "ready";
    bootstrapAbort = null;
    return nymMixFetch;
  } catch (err: unknown) {
    nymStatus = "error";
    nymError = (err instanceof Error ? err.message : String(err));
    bootstrapAbort = null;
    nymMixFetch = null;
    throw err;
  }
}

export function abortBootstrap(): void {
  if (bootstrapAbort) {
    bootstrapAbort.abort();
    bootstrapAbort = null;
  }
}

export function resetNymClient(): void {
  abortBootstrap();
  nymMixFetch = null;
  nymStatus = "idle";
  nymError = null;
}